import anime from 'animejs';
import {
  type DetailedHTMLProps,
  type HTMLAttributes,
  useCallback,
  useState,
  type VideoHTMLAttributes,
} from 'react';
import { useInViewEffect } from 'react-hook-inview';

import cn from '@/helper/cn';

import type { KeyFeatureType } from '../constants/solutions';

type KeyFeaturesSectionType = {
  title: string;
  features: KeyFeatureType[];
  video: DetailedHTMLProps<
    VideoHTMLAttributes<HTMLVideoElement>,
    HTMLVideoElement
  >;
} & HTMLAttributes<HTMLDivElement>;

const KeyFeaturesSection = ({
  features,
  title,
  ...props
}: KeyFeaturesSectionType) => {
  const { src, poster, ...videoProps } = props.video;
  const [currentTabIndex, setCurrentTabIndex] = useState(-1);

  const goToTab = useCallback(
    (index: number) => {
      // const currentTabLine = document.querySelector('.active-tab-line')

      if (index === currentTabIndex) return;

      if (index > currentTabIndex) {
        // Note: This is the tab line animation
        // anime({
        //   targets: currentTabLine,
        //   height: '100%',
        //   duration: 250,
        //   easing: 'linear',
        //   complete: () => {
        //     anime({
        //       targets: currentTabLine,
        //       height: 0,
        //       duration: 500,
        //       easing: 'linear',
        //     })
        //   },
        // })

        anime({
          opacity: 0,
          scale: [1, 0.9],
          duration: 250,
          easing: 'linear',
          complete: () => {
            setCurrentTabIndex(index);
            anime({
              opacity: 1,
              scale: [1.1, 1],
              duration: 250,
              easing: 'linear',
            });
          },
        });
      } else {
        anime({
          opacity: 0,
          scale: [1, 0.9],
          duration: 250,
          easing: 'linear',
          complete: () => {
            setCurrentTabIndex(index);
            anime({
              opacity: 1,
              scale: [1.1, 1],
              duration: 250,
              easing: 'linear',
            });
          },
        });
      }
    },
    [currentTabIndex],
  );

  // NOTE: This is the step by step animation
  // const stepByStepGoToTab = useCallback(
  //   (index: number) => {
  //     if (index > currentTabIndex) {
  //       for (let i = currentTabIndex; i <= index; i++) {
  //         setTimeout(() => {
  //           goToTab(i)
  //         }, 500 * (i - currentTabIndex)) // NOTE: Check this out!
  //       }
  //     } else {
  //       goToTab(index)
  //     }
  //   },
  //   [currentTabIndex, goToTab]
  // )

  const sectionRef = useInViewEffect(([entry], observer) => {
    if (entry.isIntersecting) {
      observer.unobserve(entry.target);
      goToTab(0);
    }

    anime.timeline({ loop: false }).add({
      targets: '#key-features-section > h2',
      opacity: [0, 1],
      translateY: [100, 0],
      duration: 1500,
      easing: 'easeOutExpo',
      delay: 200,
    });
  });

  return (
    <section
      ref={sectionRef}
      id="key-features-section"
      className="relative isolate mx-auto flex w-full max-w-container flex-col items-center gap-y-12 overflow-hidden px-10 py-10 lg:px-20 lg:pb-24"
    >
      <h2 className="text-center text-xl font-semibold text-zinc-900 opacity-0 [text-wrap:pretty] sm:text-2xl lg:text-4xl/relaxed">
        {title}
      </h2>
      <div className="flex flex-col items-center justify-between gap-12 lg:min-h-[500px] xl:flex-row xl:gap-36">
        <div className="flex flex-1 flex-col gap-2 text-left">
          {features.map((feature, index) => (
            <div key={index} className="group relative flex gap-4 pb-4">
              {index !== features.length - 1 && (
                <div className="absolute left-4 top-4 -z-10 h-full w-[1px] flex-shrink-0 -translate-x-1/2 bg-zinc-200 sm:left-6"></div>
              )}

              {index !== features.length - 1 && currentTabIndex === index && (
                <div
                  className={cn(
                    'active-tab-line absolute left-4 top-4 -z-10 w-[1px] flex-shrink-0 -translate-x-1/2 rounded-3xl bg-brand-600 sm:left-6 md:w-1',
                  )}
                />
              )}

              <div className="flex h-full w-8 flex-shrink-0 justify-center sm:w-12">
                <div
                  className={cn(
                    'flex flex-shrink-0 items-center justify-center rounded-full ring-white duration-300',
                    currentTabIndex === index
                      ? 'h-8 w-8 bg-brand-100 ring-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12'
                      : 'mt-3 h-2.5 w-2.5 cursor-pointer bg-zinc-200 ring-4 group-hover:bg-brand-600 sm:h-3.5 sm:w-3.5 lg:mt-4',
                  )}
                  onClick={() => {
                    currentTabIndex !== index && goToTab(index);
                    // stepByStepGoToTab(index)
                  }}
                >
                  <div
                    className={cn(
                      'z-10 delay-100 duration-300',
                      currentTabIndex === index
                        ? 'opacity-1 scale-100'
                        : 'scale-0 opacity-0',
                    )}
                  >
                    {feature.Icon && (
                      <feature.Icon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8" />
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex h-8 items-center justify-start sm:h-10 lg:h-12">
                  <h3
                    onClick={() => {
                      goToTab(index);
                      // stepByStepGoToTab(index)
                    }}
                    className={cn(
                      'cursor-pointer duration-300 group-hover:text-brand-600',
                      currentTabIndex === index
                        ? 'text-base text-brand-600 sm:text-lg lg:text-2xl'
                        : 'text-base text-zinc-500 sm:text-base lg:text-xl',
                    )}
                  >
                    {feature.title}
                  </h3>
                </div>

                <div
                  className={cn(
                    'flex flex-col gap-5 overflow-hidden duration-300',
                    currentTabIndex === index ? 'h-max' : 'h-0',
                  )}
                >
                  <p className="text-xs text-zinc-600 sm:text-base lg:text-lg">
                    {feature.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex h-full items-center xl:max-w-[620px]">
          <video
            id="video-player"
            playsInline
            controls={false}
            autoPlay
            muted
            loop
            className="aspect-16/10 overflow-hidden rounded-md shadow-xl md:rounded-xl"
            src={src}
            poster={poster}
            preload="false"
            {...videoProps}
          />
        </div>
      </div>
    </section>
  );
};

export default KeyFeaturesSection;
